import { useCallback, useEffect, useState } from "react";
import { apiPcfEstoque, type PcfAConfirmar } from "../lib/api";
import { type Usuario } from "../lib/auth";
import { Cabecalho } from "./ProducaoPcf";
import { MsgModal } from "../components/MsgModal";
import { Confirma } from "../components/EstoqueUi";
import { useOrdenacao, ThOrdena } from "../hooks/useOrdenacao";

// pcf_confirmacao "Confirmação de PCF" (legado novo C:\formsteel_d\pcf_confirmacao, menu producao!PCF_CONFIRMACAO):
// PCFs AGUARDANDO CONFIRMAÇÃO; Liberar (põe em produção e baixa o material), Alterar (abre a PCF), Imprimir (R1CADPCF).

interface Props {
  usuario: Usuario;
  onVoltar: () => void;
  onAlterar: (numpcf: number) => void;
  onImprimir: (numpcf: number) => void;
}

export function PcfConfirmacao({ usuario, onVoltar, onAlterar, onImprimir }: Props) {
  const [linhas, setLinhas] = useState<PcfAConfirmar[] | null>(null);
  const [sel, setSel] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [pergunta, setPergunta] = useState<"liberar" | "imprimir" | null>(null);
  const [busy, setBusy] = useState(false);
  const ord = useOrdenacao(linhas, { pcf: (l) => l.numpcf, ped: (l) => l.numped, pro: (l) => l.nompro, tipo: (l) => l.tipo, emi: (l) => l.datemi });

  // MOSTRAR-TABELA (pc:446-520)
  const carregar = useCallback(async () => {
    try {
      const r = await apiPcfEstoque.confirmacao();
      setLinhas(r.linhas);
      if (r.truncado) setErro("Quantidade de Itens é superior ao máximo permitido.");
    } catch (e) {
      setErro((e as Error).message);
    }
  }, []);
  useEffect(() => {
    void carregar();
  }, [carregar]);

  // botao-liberar (pc:180-291)
  async function liberar() {
    if (!sel) return;
    setPergunta(null);
    setBusy(true);
    try {
      const r = await apiPcfEstoque.liberar(sel);
      if (!r.ok) {
        setErro(r.erro ?? "Falha na liberação.");
        return;
      }
      await carregar();
      setPergunta("imprimir"); // "Deseja Imprimir a PCF?" (pc:278-286)
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app">
      <Cabecalho usuario={usuario} titulo="Confirmação de PCF" sub="PCFs aguardando confirmação para a produção" onVoltar={onVoltar} />
      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />
      <MsgModal texto={ok} tipo="ok" onClose={() => setOk(null)} />
      <Confirma
        texto={pergunta === "liberar" ? "Deseja realmente liberar a PCF?" : pergunta === "imprimir" ? "Deseja Imprimir a PCF?" : null}
        onSim={() => {
          if (pergunta === "liberar") void liberar();
          else if (sel) {
            setPergunta(null);
            onImprimir(sel);
          }
        }}
        onNao={() => {
          if (pergunta === "imprimir") setOk(`PCF ${sel} liberada para a produção.`);
          setPergunta(null);
          if (pergunta === "imprimir") setSel(null);
        }}
      />
      <div className="pcf-form">
        <section className="pcf-sec">
          <div className="tabela-wrap">
            <table className="tabela-pcfs">
              <thead>
                <tr>
                  <ThOrdena ord={ord} col="pcf" className="num">Número PCF</ThOrdena>
                  <ThOrdena ord={ord} col="ped" className="num">Num. Pedido</ThOrdena>
                  <ThOrdena ord={ord} col="pro">Produto</ThOrdena>
                  <ThOrdena ord={ord} col="tipo">Tipo</ThOrdena>
                  <ThOrdena ord={ord} col="emi">Data Emissão</ThOrdena>
                </tr>
              </thead>
              <tbody>
                {linhas && linhas.length === 0 && (
                  <tr><td colSpan={5} className="plan-vazio">Nenhuma PCF aguardando confirmação.</td></tr>
                )}
                {ord.linhas.map((l) => (
                  <tr key={l.numpcf} className={"row-clickable" + (sel === l.numpcf ? " sel" : "")} onClick={() => setSel(sel === l.numpcf ? null : l.numpcf)}>
                    <td className="num">{l.numpcf}</td>
                    <td className="num">{l.numped || ""}</td>
                    <td>{l.nompro}</td>
                    <td>{l.tipo}</td>
                    <td>{l.datemi}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <div className="pcf-acoes">
          <button className="btn-acao verde" disabled={!sel || busy} onClick={() => setPergunta("liberar")}>Liberar</button>
          <button className="btn-secundario" disabled={!sel || busy} onClick={() => sel && onAlterar(sel)}>Alterar</button>
          <button className="btn-secundario" disabled={!sel || busy} onClick={() => sel && onImprimir(sel)}>Imprimir</button>
          <span className="spacer" />
          <button className="btn-secundario" onClick={onVoltar}>Sair</button>
        </div>
      </div>
    </div>
  );
}
